/* eslint-disable @typescript-eslint/no-explicit-any */
import { ahuBoardApi } from 'app/services/ahuBoardApi';

/**
 * Phase 2 (dev-sync) — FAN selector VFD input tab data layer.
 *
 * Backbone source: fan.selector.js — _vfdByMotorUrl (7866-7896),
 * loadVfdSmartlist (7974-8054), _buildVfdPayload (8341-8387),
 * computeVFDPosition (8392-8409), loadVfdSections (8476-8502),
 * updateVfdQtyTotal (8554-8594), loadVfdMountingArrangements (8598-8645),
 * _getVFD (8763-8834), _refreshVfdModelDropdown (8519-8528).
 *
 * ★ DATA-SAFETY: every GET here is read-only. The legacy view caches each
 * response by URL in stateStorage ("fan-rating-dialog" bucket) — RTKQ's
 * cache-by-arg (the arg IS the URL) + lazy trigger(url, preferCache)
 * replicates that. The POST compute_position is a STATELESS preview
 * — the backend builds transient in-memory models and never saves (see
 * vfd_viewset.py AHUVFDComputePositionViewSet docstring).
 *
 * B3 adds the PERSIST writes (_persistVfdAccessory js:8688-8745): POST/PATCH/
 * DELETE /ahu/vfds/ + PATCH /ahu/positions/. They fire ONLY from the Select
 * path of the fan selector (fanVfdPersist.ts) — never on open/browse/Rate.
 */

export const VFD_CMS_BASE = '/api/v1.0/cms/1/v1.0.0/ahu/motor_controllers/vfd';
/** vfd CMS params doc (legacy fn['current.cms.vfd'] — self.vfdCms). Also
 * accepts `?voltage_id=` for the Model-dropdown refresh (js:8519-8528). */
export const VFD_CMS_PARAMS_URL = `${VFD_CMS_BASE}/params/`;
/** unfiltered catalog total for the smartlist badge (js:7981-7982). */
export const VFD_CMS_COUNT_URL = `${VFD_CMS_BASE}/all/count/`;
/** by_motor list/filter endpoint — NO mfg path segment; mfg = the cms pk `1`. */
export const VFD_BY_MOTOR_URL = `${VFD_CMS_BASE}/by_motor/`;

/** isNull (fan.selector.js:8837-8839). */
export const isVfdNone = (v: unknown): boolean =>
  v === undefined || v === null || v === 'None' || v === '';

/** omitNulls (fan.selector.js:8695-8702) — strip null/undefined keys before
 * the vfds create/PATCH bodies: the backend does an unguarded
 * `int(request.data.get(...))` on configuration_id (vfd_viewset.py create:151,
 * update:218) and casing_mount_id (create:142, update:242), so an explicit
 * null 500s. (master_vfd_id / mounting_arrangement_id / fan_motor_id ARE
 * None-guarded backend-side.) */
export function omitNulls(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  Object.keys(obj).forEach((key) => {
    if (obj[key] !== null && obj[key] !== undefined) out[key] = obj[key];
  });
  return out;
}

/** rows of the vfd CMS params doc lists (manufacturer/series/model/…). */
export interface VfdCmsRow {
  id?: number;
  name?: string;
  value?: string;
  series?: string;
  model?: string;
  fla?: number | string;
  [key: string]: unknown;
}

/** GET …/vfd/params/ → {vfd:{manufacturer, series, model, description, power,
 * voltage, nema_encl_type, mounting_plane, casing_mount, fla, wiring,
 * supplied_by, configuration, mounting_arrangement}}. */
export interface VfdCmsDoc {
  vfd?: {
    manufacturer?: VfdCmsRow[];
    series?: VfdCmsRow[];
    model?: VfdCmsRow[];
    description?: VfdCmsRow[];
    power?: VfdCmsRow[];
    voltage?: VfdCmsRow[];
    nema_encl_type?: VfdCmsRow[];
    mounting_plane?: VfdCmsRow[];
    casing_mount?: VfdCmsRow[];
    fla?: VfdCmsRow[];
    wiring?: VfdCmsRow[];
    supplied_by?: VfdCmsRow[];
    configuration?: VfdCmsRow[];
    mounting_arrangement?: VfdCmsRow[];
  };
}

/** by_motor result row (master_cms_serializers.py:414-445). */
export interface VfdByMotorItem {
  id: number;
  spn?: string;
  mfg_id?: number | null;
  series_id?: number | null;
  power?: number | string | null;
  voltage?: string | null;
  model?: string | null;
  fla?: number | string | null;
  fla_input?: number | string | null;
  nema_encl_type_id?: number | null;
  power_id?: number | null;
  voltage_id?: number | null;
  nema_encl_type_name?: string | null;
  mfg_name?: string | null;
  series_name?: string | null;
  description_id?: number | null;
  description_name?: string | null;
  depth?: number | null;
  mpn?: string | null;
  frame?: string | null;
  [key: string]: unknown;
}

export interface VfdByMotorResponse {
  required_fla?: number | null;
  vfd_qty?: number | null;
  count?: number | null;
  filtered_count?: number | null;
  results?: VfdByMotorItem[];
}

/** GET …/vfd/mounting_arrangements/?qty_total=N row. */
export interface VfdMountingArrangement {
  id: number;
  name?: string;
  value?: string;
  qty_total?: number;
  order_id?: number;
  [key: string]: unknown;
}

/** GET /api/v1.0/ahu/vfds/ row (AHUAccessoryVFDSerializer — vfd_serializers.py). */
export interface AhuVfdRow {
  id: number;
  master_vfd_id?: number | null;
  vfd_params?: string | null;
  section_id?: number | null;
  position_id?: number | null;
  configuration_id?: number | null;
  casing_mount_id?: number | null;
  wiring_id?: number | null;
  mounting_arrangement_id?: number | null;
  supplied_by_id?: number | null;
  voltage_id?: number | null;
  fan_motor_id?: number | null;
  /** dev-sync I — the SECOND, often-NULL fan FK (see vfd_viewset.py
   *  filter_fan_id on merged dev); matched alongside fan_motor_id. */
  fan_id?: number | null;
  spacing_horizontal?: number | null;
  spacing_vertical?: number | null;
  housed_in_enclosure?: boolean | null;
  enc_width?: number | null;
  enc_height?: number | null;
  fla?: number | string | null;
  fla_input?: number | string | null;
  bank_width?: number | null;
  bank_height?: number | null;
  [key: string]: unknown;
}

/** GET /api/v1.0/ahu/sections/?ahu_id= row (AHUSectionSerializer,
 * section_serializers.py — the select consumes id + label). */
export interface AhuSectionRow {
  id: number;
  label?: string | null;
  tag?: string | null;
  [key: string]: unknown;
}

/** {vfd, position} payload shared by the compute_position preview (B2) and
 * the persist calls on Select (B3) — _buildVfdPayload (js:8341-8387). */
export interface VfdPayload {
  vfd: {
    section_id: number | string | null;
    fan_motor_id: number | null;
    master_vfd_id: number | string | null;
    configuration_id: number | null;
    mounting_arrangement_id: number | string | null;
    casing_mount_id: number | string | null | undefined;
    wiring_id: number | string | null;
    supplied_by_id: number | string | null;
    voltage_id: number | string | null;
    spacing_horizontal: number;
    spacing_vertical: number;
    housed_in_enclosure: boolean;
    enc_width: number | null;
    enc_height: number | null;
    vfd_params: string;
  };
  position: {
    mounting_plane_id: number | string | null;
    default_pos_x: boolean;
    default_pos_y: boolean;
    x: number | string | null;
    y: number | string | null;
  };
}

/**
 * _buildVfdPayload (js:8341-8387), key-for-key. Notes on the legacy literal:
 *  - fan_motor_id appears TWICE (model_data.model.id at :8355, then
 *    current_model?.id at :8377) — last wins, so the caller passes the
 *    current fan model id.
 *  - casing_mount_id is deliberately NOT isNull-guarded (matches :8362).
 *  - housed_in_enclosure is the live checkbox in Backbone (:8346-8348);
 *    React state IS the checkbox state.
 *  - the `mountingPlaneId` local computed at :8349 is dead — the returned
 *    position uses vfd_position.mounting_plane_id directly (:8382).
 * Nulls are NOT omitted here — compute_position takes them as-is; the
 * null-omission for create/PATCH is the persist step's job (B3).
 */
export function buildVfdPayload(
  vfd: Record<string, any>,
  position: Record<string, any>,
  vfdParams: Record<string, any>,
  fanMotorId: number | null
): VfdPayload {
  return {
    vfd: {
      section_id: vfd.section_id ?? null,
      fan_motor_id: fanMotorId ?? null,
      master_vfd_id: vfd.master_vfd_id ?? null,
      configuration_id: isVfdNone(vfd.configuration_id) ? null : Number(vfd.configuration_id),
      mounting_arrangement_id: isVfdNone(vfd.mounting_arrangement_id)
        ? null
        : vfd.mounting_arrangement_id,
      casing_mount_id: vfd.casing_mount_id,
      wiring_id: isVfdNone(vfd.wiring_id) ? null : vfd.wiring_id,
      supplied_by_id: isVfdNone(vfd.supplied_by_id) ? null : vfd.supplied_by_id,
      voltage_id: isVfdNone(vfdParams.voltage_id) ? null : vfdParams.voltage_id,
      spacing_horizontal: Number(vfd.spacing_horizontal),
      spacing_vertical: Number(vfd.spacing_vertical),
      housed_in_enclosure: Boolean(vfd.housed_in_enclosure),
      enc_width: isVfdNone(vfd.enc_width) ? null : Number(vfd.enc_width),
      enc_height: isVfdNone(vfd.enc_height) ? null : Number(vfd.enc_height),
      vfd_params: JSON.stringify(vfdParams),
    },
    position: {
      mounting_plane_id: position.mounting_plane_id ?? null,
      default_pos_x: position.default_pos_x !== false,
      default_pos_y: position.default_pos_y !== false,
      x: position.x ?? null,
      y: position.y ?? null,
    },
  };
}

/** POST /api/v1.0/ahu/vfds/compute_position response
 * (vfd_viewset.py AHUVFDComputePositionViewSet.create, 471-789). */
export interface VfdComputeResponse {
  vfd?: {
    section_id?: number | null;
    master_vfd_id?: number | null;
    mounting_arrangement_id?: number | null;
    width?: number | null;
    height?: number | null;
    default_width?: number | null;
    default_height?: number | null;
    spacing_horizontal?: number | null;
    spacing_vertical?: number | null;
    housed_in_enclosure?: boolean;
    bank_width?: number | null;
    bank_height?: number | null;
    enc_width?: number | null;
    enc_height?: number | null;
    vfd_qty?: number | null;
    vfd_bank_positions?: Array<{ x: number; y: number }> | null;
    distance_to_downstream_wall?: number | null;
    distance_to_roof?: number | null;
    distance_to_right_wall?: number | null;
    fla?: number | string | null;
    [key: string]: unknown;
  };
  position?: {
    x?: number | null;
    y?: number | null;
    default_pos_x?: boolean;
    default_pos_y?: boolean;
    mounting_plane_id?: number | null;
    [key: string]: unknown;
  };
}

/**
 * by_motor URL builder — mirrors _vfdByMotorUrl (js:7866-7896): the motor-derived
 * params first, then the manual smartlist filters (_buildVfdSmartlistQS
 * js:7924-7942). Null/undefined/'None' (and '') are omitted. Note the legacy
 * string sends voltage_id twice (motor-derived + the hidden #voltage_id filter);
 * Django QueryDict.get takes the LAST one — both are preserved here for parity.
 */
export function buildVfdByMotorUrl(
  motorParams: {
    motor_fla?: number | string | null;
    configuration?: string | null;
    fan_qty?: number | null;
    mtr_mfg_id?: number | string | null;
    mtr_series_id?: number | string | null;
    voltage_id?: number | string | null;
  },
  smartlistFilters: {
    mfg_id?: number | string | null;
    series_id?: number | string | null;
    model?: string | null;
    rating?: number | string | null;
    description_id?: number | string | null;
    voltage_id?: number | string | null;
  }
): string {
  const parts: string[] = [];
  const push = (obj: Record<string, unknown>) => {
    for (const [k, val] of Object.entries(obj)) {
      if (isVfdNone(val)) continue;
      parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(val))}`);
    }
  };
  push(motorParams);
  push(smartlistFilters);
  return VFD_BY_MOTOR_URL + (parts.length ? `?${parts.join('&')}` : '');
}

/** qty_total URL (js:8566-8567) — param name is `vfd_configuration` here,
 * `configuration` on by_motor (both take the string value, e.g. one_to_one). */
export function buildVfdQtyTotalUrl(configValue: string, fanQty: number): string {
  return `/api/v1.0/ahu/vfds/qty_total?vfd_configuration=${encodeURIComponent(configValue)}&fan_qty=${encodeURIComponent(String(fanQty))}`;
}

/** mounting arrangements filtered by total VFD quantity (js:8601-8603). */
export function buildVfdMountingArrangementsUrl(qtyTotal: number): string {
  return `${VFD_CMS_BASE}/mounting_arrangements/?qty_total=${encodeURIComponent(String(qtyTotal))}`;
}

/** Dev-sync H — the VFD-to-Motor Configuration filter endpoint (NEW on merged
 * dev: rest_ahu/urls.py `vfds/configurations/` → VFDConfigurationFilterViewSet,
 * MasterAHUVFDConfiguration filtered through the
 * MasterAHUVFDMfgSeriesVFDConfiguration mapping table). The dialog calls it
 * ONLY once BOTH vfd mfg + series are picked (fan.selector.js
 * loadVfdConfigurations on merged dev); rows are {id, name, value}. */
export function buildVfdConfigurationsUrl(
  mfgId: number | string,
  seriesId: number | string
): string {
  return (
    '/api/v1.0/ahu/vfds/configurations/?vfd_mfg_id=' +
    encodeURIComponent(String(mfgId)) +
    '&vfd_series_id=' +
    encodeURIComponent(String(seriesId))
  );
}

// 'AhuVfds' tags the ahu-wide VFD list (the existing-VFD detector) so the B3
// persist mutations refresh it — a reopened selector must not act on a stale
// existing-id (deleted → PATCH 400s; created → duplicate POST).
export const fanVfdApi = ahuBoardApi.enhanceEndpoints({ addTagTypes: ['AhuVfds'] }).injectEndpoints({
  endpoints: (builder) => ({
    // vfd CMS params doc, by full URL so the voltage-filtered Model refresh
    // (params/?voltage_id=N) shares the endpoint + per-URL cache.
    getVfdCmsParams: builder.query<VfdCmsDoc, string>({
      query: (url) => url,
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // unfiltered smartlist badge total → {count} (js:7981-7982, 8040-8041).
    getVfdCmsCount: builder.query<{ count: number | null }, void>({
      query: () => VFD_CMS_COUNT_URL,
      transformResponse: (res: unknown): { count: number | null } => ({
        count: (res as { count?: number } | null)?.count ?? null,
      }),
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // by_motor smartlist page, by full URL (incl. &page=N) — the RTKQ per-URL
    // cache mirrors the legacy stateStorage cache-by-URL.
    getVfdByMotor: builder.query<VfdByMotorResponse, string>({
      query: (url) => url,
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // {qty_total} for the readonly Quantity field (vfd_viewset.py:435-468).
    getVfdQtyTotal: builder.query<{ qty_total: number | null }, string>({
      query: (url) => url,
      transformResponse: (res: unknown): { qty_total: number | null } => ({
        qty_total: (res as { qty_total?: number } | null)?.qty_total ?? null,
      }),
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // mounting arrangements valid for a qty_total (motor_controllers viewset).
    getVfdMountingArrangements: builder.query<VfdMountingArrangement[], string>({
      query: (url) => url,
      transformResponse: (res: unknown): VfdMountingArrangement[] =>
        Array.isArray(res) ? (res as VfdMountingArrangement[]) : ((res as any)?.results ?? []),
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // Dev-sync H — configurations mapped to a vfd mfg/series pair (the
    // VFD-to-Motor Configuration dropdown filter). Legacy fetches with a plain
    // fn.uni_tools.get (NO cache) on every mfg/series change — the lazy
    // trigger's default preferCacheValue=false keeps that network behaviour.
    getVfdConfigurations: builder.query<VfdCmsRow[], string>({
      query: (url) => url,
      transformResponse: (res: unknown): VfdCmsRow[] =>
        Array.isArray(res) ? (res as VfdCmsRow[]) : ((res as any)?.results ?? []),
    }),

    // persisted VFD accessories for this AHU (vfd_viewset.py:47-55 — unit_id
    // filters section__tunnel__ahu__id). The fan match (fan_motor_id ===
    // <fan id>) happens client-side, same as the legacy stateStorage scan.
    getAhuVfds: builder.query<AhuVfdRow[], number>({
      query: (unitId) => `/api/v1.0/ahu/vfds/?unit_id=${unitId}`,
      transformResponse: (res: unknown): AhuVfdRow[] =>
        Array.isArray(res) ? (res as AhuVfdRow[]) : ((res as any)?.results ?? []),
      providesTags: ['AhuVfds'],
    }),

    // the AHU's sections for the Section select (loadVfdSections js:8476-8502;
    // section_viewset.py AHUSectionsViewSet.list, ahu_id branch 238-305).
    getAhuSections: builder.query<AhuSectionRow[], number>({
      query: (ahuId) => `/api/v1.0/ahu/sections/?ahu_id=${ahuId}`,
      transformResponse: (res: unknown): AhuSectionRow[] =>
        Array.isArray(res) ? (res as AhuSectionRow[]) : [],
    }),

    // STATELESS bank/position preview (computeVFDPosition js:8392-8409) —
    // the backend builds transient models and saves nothing, so no cache
    // invalidation. Stale responses are guarded caller-side (reqId).
    postVfdComputePosition: builder.mutation<VfdComputeResponse, VfdPayload>({
      query: (payload) => ({
        url: '/api/v1.0/ahu/vfds/compute_position',
        method: 'POST',
        body: payload,
      }),
    }),

    // ---- B3 persist writes (_persistVfdAccessory js:8688-8745). Bodies must
    // be omitNulls'd by the caller (see omitNulls above). ----

    // CREATE step 1 (js:8720-8723) → serializer row incl. the new id
    // (position id == vfd id, vfd_viewset.py create:175-185).
    postAhuVfd: builder.mutation<AhuVfdRow, Record<string, unknown>>({
      query: (body) => ({ url: '/api/v1.0/ahu/vfds/', method: 'POST', body }),
      invalidatesTags: ['AhuVfds'],
    }),

    patchAhuVfd: builder.mutation<AhuVfdRow, { id: number; data: Record<string, unknown> }>({
      query: ({ id, data }) => ({
        url: `/api/v1.0/ahu/vfds/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        'AhuVfds',
        { type: 'AhuAccessories' as const, id: `vfds-${id}` },
      ],
    }),

    // PATCH ahu/positions/<id>. ahuBoardFormsApi.patchAhuPosition hits the
    // same URL but lives on a DIFFERENT createApi and cannot invalidate this
    // api's `position-<id>` cache (getAccessoryPosition) — the VFD tab's
    // existing-VFD init reads that cache with preferCacheValue, so a stale
    // position would echo the pre-persist x/y on reopen.
    patchAhuVfdPosition: builder.mutation<
      Record<string, unknown>,
      { id: number; data: Record<string, unknown> }
    >({
      query: ({ id, data }) => ({
        url: `/api/v1.0/ahu/positions/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'AhuAccessories' as const, id: `position-${id}` },
      ],
    }),

    // DELETE on has_vfd uncheck (js:8737) — vfd_viewset.py destroy (276-302)
    // removes the position row too. Deliberately does NOT invalidate the
    // per-id `vfds-<id>` / `position-<id>` tags: their holders are
    // page-lifetime subscriptions (the boot-draw fetches in
    // useAhuBoardBootstrap + FanVfdTab's lazy position read), and refetching
    // a just-DELETED id is a guaranteed 400/404 — pure console/network noise.
    // Nothing re-reads the dead entries afterwards: the row leaves every list
    // via the 'AhuVfds' invalidation + the persist step's
    // getSectionAccessories refetch, so no form can mount against it.
    deleteAhuVfd: builder.mutation<unknown, number>({
      query: (id) => ({ url: `/api/v1.0/ahu/vfds/${id}`, method: 'DELETE' }),
      invalidatesTags: ['AhuVfds'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetVfdCmsParamsQuery,
  useLazyGetVfdCmsParamsQuery,
  useLazyGetVfdCmsCountQuery,
  useLazyGetVfdByMotorQuery,
  useLazyGetVfdQtyTotalQuery,
  useLazyGetVfdMountingArrangementsQuery,
  useLazyGetVfdConfigurationsQuery,
  useGetAhuVfdsQuery,
  useGetAhuSectionsQuery,
  usePostVfdComputePositionMutation,
} = fanVfdApi;
